import { CancellationError, reportException, SkywardException } from "#skyward_error";
import { getPrefersReducedMotion, processViewPartHtml } from "#skyward_global";
import { isContentLoading, notifyResize } from "#skyward_window";
import { effectScope, EffectScope, onScopeDispose } from 'vue';
import { isDeferred } from "./skyward.base.extensions.promise";
import debounce from "./skyward.ui.debounce";
import { Dictionary, Skyward } from "./types";
export { lazy } from "./skyward.base.extensions.lazy";
import { getIdGenerator } from "./skyward.base.extensions.idGenerator";
export { getIdGenerator };

// Copied from https://github.com/microsoft/TypeScript/blob/main/src/lib/es2021.promise.d.ts.
// The file includes both AggregateError, which we polyfill, and Promise.any, which we don't, so we
// can't import it directly.
declare global
{
    interface AggregateError extends Error {
        errors: any[];
    }

    interface AggregateErrorConstructor {
        new (errors: Iterable<any>, message?: string): AggregateError;
        (errors: Iterable<any>, message?: string): AggregateError;
        readonly prototype: AggregateError;
    }

    var AggregateError: AggregateErrorConstructor;

    interface ArrayConstructor
    {
        chunk<T>(iter: Iterable<T>, count: number): T[][];
    }
}

interface JQueryStaticInternals
{
    propHooks: Dictionary<string, { set: (el: Element, value: any) => void; }>;
    attrHooks: Dictionary<string, { set: (el: Element, value: any) => void; }>;
    expr: {
        pseudos: Dictionary<string, (el: HTMLElement) => boolean>;
    };
    find: Function & {
        tokenize(selector: string): { value: string; }[][];
        matchesSelector(el: Element, selector: string): boolean;
    };
    _evalUrl(url: string, options: object, doc: Document): JQuery.jqXHR;
}

interface JQueryUIStaticInternals
{
    resizable: JQueryUI.Resizable;
}

const skyward = window.skyward ?? (window.skyward = {} as unknown as Skyward);

const autoIdGenerator = getIdGenerator("autoid");

(function ($)
{
    $.safeRound = function (v)
    {
        var number = Math.round(v);
        if (isNaN(number))
        {
            return null;
        }
        return number;
    };

    // used to get the first non-null or undefined value
    // false, 0, and "" will be returned
    $.nullCoalesce = function ()
    {
        var len = arguments.length;
        for (var i = 0; i < len; i++)
        {
            if (arguments[i] !== null && arguments[i] !== undefined)
            {
                return arguments[i];
            }
        }
        return null;
    };

    // used to reset the data of an element without using removeData
    // removeData is dangerous because the next time .data() is called on an element
    // the original data is reset
    $.fn.setAllDataToNull = function ()
    {
        return this.each(function ()
        {
            var el = $(this);
            var data = el.data();

            $.each(data, function (key)
            {
                data[key] = null;
            });

            el.data(data);
        });
    };

    $.fn.encodedVal = function ()
    {
        let val = this.val();
        if (Array.isArray(val))
        {
            val = val.join(",");
        }
        return encodeHtml(val ?? "");
    };

    $.fn.sortChildrenByAction = function (action, desc)
    {
        let i, els = this.children().toArray().sort(function (a, b) { return action(a, b, desc); });
        for (i = 0; i < els.length; i++)
        {
            this.prepend($(els[i]).detach());
        }
        return this;
    };

    // Add a hook to $().prop("checked") to trigger "jschange" whenever an input is programatically changed.
    ($ as unknown as JQueryStaticInternals).propHooks.checked = {
        set: function (elParam, value)
        {
            const el = elParam as HTMLInputElement;
            if (el.checked !== value)
            {
                el.checked = value;
                var jqEl = $(el);
                jqEl.trigger('jschange');
            }
        }
    };
    // Even though using $().attr() isn't best practice, override the setter in case it -does- get used somewhere.
    ($ as unknown as JQueryStaticInternals).attrHooks.checked = {
        set: function (elParam, value)
        {
            const el = elParam as HTMLInputElement;
            if (el.checked !== value)
            {
                el.checked = value;
                var jqEl = $(el);
                jqEl.trigger('jschange');
            }
        }
    };

    $.wait = function (totalTime, updateInterval)
    {
        return $.Deferred(function (deferred)
        {
            if (updateInterval)
            {
                var intervalId = setInterval(function () { deferred.notify(); }, updateInterval);
            }

            setTimeout(function () { clearInterval(intervalId); deferred.resolve(); }, totalTime);
        });
    };

    function hasSetSelectionRange(input: Element): input is HTMLInputElement
    {
        return (input as any).setSelectionRange;
    }

    $.fn.selectRange = function (start, end)
    {
        if (!end) end = start;
        return this.each(function ()
        {
            if (hasSetSelectionRange(this))
            {
                this.focus();
                this.setSelectionRange(start, end as number | null);
            }
        });
    };

    /**
    * Change the tag name of a set of jQuery elements.
    * WARNING: Existing elements will be replaced by a new set of elements.
    *          Any events or $.data associated with the elements will be lost.
    * @param {string} newTagName The new tag name
    * @returns {jQuery} Returns a new jQuery collection with all elements updated to have the new tag name.
    */
    $.fn.replaceTagNames = function (newTagName)
    {
        let i = this.length;

        while (i--)
        {
            const element = this[i];
            const $element = this.eq(i);
            const attributes = element.attributes;
            const newElement = document.createElement(newTagName);

            for (let a = attributes.length - 1; a >= 0; a--)
            {
                const attribute = attributes[a];
                newElement.setAttribute(attribute.name, attribute.value);
            }

            newElement.innerHTML = element.innerHTML;
            $element.after(newElement).remove();
            this[i] = newElement;
        }
        return this;
    };

    // jquery find statements execute much more efficiently if the root element has an id.
    (function ()
    {
        $.fn.addIds = function ()
        {
            for (let i = 0; i < this.length; i++)
            {
                if (this[i].id === "") { this[i].id = autoIdGenerator.getNewId(); }
            }
            return this;
        };
    })();

    /**
     * Find the label for this element (that is, the label element whose `for` attribute matches the `id` attribute
     * of the first element in the jQuery array.)
     */
    $.fn.findLabel = function ()
    {
        const parentContainer = this.parents().last();
        return $.findLabel(this.attr("id")!, parentContainer);
    };

    /**
    * Determine if this element is a valid "link" element.
    */
    $.fn.isLink = function ()
    {
        const role = this.attr("role");
        if (role === "link" || (this.is("a") && (!role || role === "menuitem")))
        {
            return true;
        }

        return false;
    };

    /**
     * Find the label for the element with the given ID (that is, the label element whose `for` attribute matches the provided ID).
     * @param {string} forId the ID to search for references to
     */
    $.findLabel = function (forId: string, container?: JQuery<HTMLElement>)
    {
        let label = $();
        if (!forId)
        {
            return label;
        }

        if (!container || container.length === 0)
        {
            container = $(document.body);
        }

        label = container.find(`label[for="${CSS.escape(forId)}"]`) as JQuery<HTMLLabelElement>
        if (label.length === 0)
        {
            label = container.find(`.decorativeLabel[data-validate-text-for="${CSS.escape(forId)}"]`) as JQuery<HTMLLabelElement>
        }
        if (label.length === 0)
        {
            label = container.find(`.decorativeLabel[data-group-id="${CSS.escape(forId)}"]`) as JQuery<HTMLLabelElement>
        }

        return label;
    };

    /**
     * Converts a jQuery collection to a string of their outer HTML.
     * Example usage: var htmlString = $(someElements).toOuterHtmlString();
     * @returns {string} Concatenated outerHTML for all elements in the collection.
     */
    $.fn.toOuterHtmlString = function ()
    {
        return this.map(function () { return this.outerHTML; }).get().join('');
    };

    /**
     * Get the ID prefix and unprefixed ID of this element, and return them separately.
     * @param {string|undefined} prefix Optional prefix to use rather than finding it from the DOM.
     * @returns {{id: string|null, prefix?: string|null|undefined}}
     */
    $.fn.getIdAndPrefix = function (prefix = null)
    {
        var id = this.attr('id');
        var dovPrefix = prefix ?? this.getPrefix();
        if (!id)
        {
            return { id: null, prefix: dovPrefix };
        }
        if (!id.startsWith(dovPrefix))
        {
            console.warn(`something is weird: expected the prefix "${dovPrefix}" but couldn't find one on the id "${id}"`, this);
            return { id, prefix: "" };
        }
        return { id: id.slice(dovPrefix.length), prefix: dovPrefix };
    };

    (function ()
    {
        // Based on https://github.com/jquery/sizzle/blob/4194dc46/src/sizzle.js#L76
        // http://www.w3.org/TR/css3-selectors/#whitespace
        const whitespace = "[\\x20\\t\\r\\n\\f]";
        // https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
        const identifier = "(?:\\\\[\\da-fA-F]{1,6} ?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+";
        // http://www.w3.org/TR/selectors/#attribute-selectors
        const attributes = new RegExp(("\\[ *id *" +
            // Operator (capture 1): only equal, not-equal, starts-with, and starts-with-followed-by-dash
            "([^|!]?=) *" +
            // "Attribute (capture 2): values must be CSS identifiers or strings
            "('(?:\\\\.|[^\\\\'])*'|\"(?:\\\\.|[^\\\\\"])*\"|" + identifier + ")" +
            " *\\]")
            .replace(/ /g, whitespace), "g");

        /**
         * Find an element by CSS selector and ID prefix. All ID references in the selector will be automatically substituted
         * to add the prefix.
         * The prefix can be left undefined, in which case the current element's prefix will be used.
         * @param {string} selector A CSS selector
         * @param {string|undefined} prefix Optional prefix to use
         * @returns {JQuery} The found elements
         */
        $.fn.findPrefixed = function (selector, prefix = null)
        {
            const dovPrefix = prefix ?? this.getPrefix();
            return this.find(selector.replace(/#/g, `#${dovPrefix}`).replace(attributes, (_, equals, ident) =>
            {
                const quote = ident[0];
                return quote == "'" || quote == '"'
                    ? `[id${equals}${quote}${dovPrefix}${ident.slice(1, -1)}${quote}]`
                    : `[id${equals}${dovPrefix}${ident}]`;
            }));
        };
        $.fn.findByIdWithUnknownPrefix = function (id)
        {
            const candidates = this.find(`[id$=${CSS.escape(id)}]`);
            if (candidates.length === 0)
            {
                return candidates;
            }
            return candidates.filter(function ()
            {
                return $(this).getIdAndPrefix().id === id;
            });
        };
    })();

    /**
     * Get an element's ID prefix.
     * @returns {string}
     */
    $.fn.getPrefix = function ()
    {
        return this.closest("[data-id-prefix]").data("idPrefix") ?? "";
    }

    // Some Android keyboard and browser combinations send "unspecified" for all keydown events.
    // Example: Android 36 Google APIs emulator image, default GBoard + Chrome
    const binding = window._skyward.global.isAndroidApp ? "keyup" : "keydown";

    // Add a custom event type that fires when you press the "enter" key.
    // See also jQuery's implementation of "mouseenter", on which is this based: https://github.com/jquery/jquery/blob/main/src/event.js#L759
    $.event.special.enter = {
        delegateType: binding, // delegated event handlers should use a native keydown event
        bindType: binding, // regular event handlers should use a native keydown event
        handle: function (event)
        {
            const handleObj = event.handleObj;
            // avoiding skyward.keys.getKeyNamesFromEvent due to possible performance considerations
            const eventKeyCode = event.keyCode || event.which;
            if (eventKeyCode === 13 /* enter key */)
            {
                return handleObj.handler.apply(this, arguments as unknown as [any, ...any[]]);
            }
            return undefined;
        }
    };

    /**
     * @description $.whenAll() accepts an array of promises and returns a promise combining them, like Promise.all().
     * It's recommended to use this instead of $.when if you are passing in a variable number of promises, as it avoids some
     * corner cases found in $.when().
     * Do not use this with vanilla Promises. Use Promise.all() instead. Vanilla promises are passed through without being resolved.
     * @param {Array<JQuery.Promise>} promises An array of promises.  $.whenAll will resolve when every promise in the array succeeds, or fail as soon as one of them fails.
     * @returns {JQuery.Promise} The combined promise.  It will resolve with an array containing the resolved values of the input promises.
     */
    // Details about $.when's corner cases:
    // Deferreds, unlike Promises, can be resolved with multiple values.  These are passed as separate arguments to .then() and .done().
    // If you pass in a single promise with multiple results to $.when, it will pass that through.
    // If you pass in multiple promises with single results, it will return each result as a separate argument.
    // However, if one of the promises has multiple results, it will be wrapped in an array.
    // This makes it impossible to handle all cases uniformly, as you don't know whether your arguments came from multiple
    // promises or multiple results, or whether they need to be unwrapped.
    $.whenAll = function (promises)
    {
        var count = promises.length;
        var results = new Array(promises.length);
        var combined = $.Deferred();
        promises.forEach(function (promise, i)
        {
            if (isDeferred(promise))
            {
                promise.done(function (result)
                {
                    results[i] = result;
                    count -= 1;
                    if (count === 0)
                    {
                        combined.resolve(results);
                    }
                });
                promise.fail(function (error)
                {
                    combined.reject(error);
                });
            }
            else if (promise instanceof Promise)
            {
                promise.then(
                    function (result)
                    {
                        results[i] = result;
                        count -= 1;
                        if (count === 0)
                        {
                            combined.resolve(results);
                        }
                    },
                    function (error)
                    {
                        combined.reject(error);
                    }
                );
            }
            else
            {
                results[i] = promise;
                count -= 1;
            }
        });
        if (count === 0)
        {
            combined.resolve(results);
        }
        return combined.promise();
    };

    // Returns a resolved promise when any of the passed in promises are resolved, but waits for all to complete before resolving.
    $.whenAllSettled = function (promises)
    {
        const combined = $.Deferred();
        if (promises.length == 0)
        {
            combined.resolve();
            return combined.promise();
        }

        Promise.all(promises.map(reflect)).then(function (results)
        {
            const success = results.filter(x => x.status === "fulfilled");
            if (success.length > 0)
            {
                combined.resolve(results);
            }
            else
            {
                combined.reject(new AggregateError(results.map(result => result.data)));
            }
        });

        return combined.promise();

        function reflect<T>(promise: T|PromiseLike<T>)
        {
            return Promise.resolve(promise).then(function (v) { return { data: v, status: "fulfilled" }; },
                function (e) { return { data: e, status: "rejected" }; });
        }
    }
})(jQuery);

// Polyfill for Safari < 14.0
// based on https://github.com/niksy/aggregate-error-ponyfill
if (!window.AggregateError)
{
    window.AggregateError = class extends Error
    {
        errors: Error[];
        constructor(errors: Iterable<any>, message: string | undefined = "")
        {
            super(message);
            if (Array.isArray(errors) ||
                window?.Symbol?.iterator && (typeof errors?.[window.Symbol.iterator]) === "function")
            {
                this.errors = Array.from(errors);
                return;
            }
            throw new TypeError(`${errors} is not an iterable`);
        }
    } as any;
}

String.prototype.padRight = function (length, value)
{
    var t = "" + this;
    while (t.length < length)
    {
        t += value;
    }
    return t;
};

String.prototype.padLeft = function (length, value)
{
    var t = "" + this;
    while (t.length < length)
    {
        t = value + t;
    }
    return t;
};

String.prototype.replaceAt = function (index, character)
{
    //http://stackoverflow.com/questions/1431094/how-do-i-replace-a-character-at-a-particular-index-in-javascript
    return this.substr(0, index) + character + this.substr(index + character.length);
};

String.prototype.toDisplayName = function ()
{
    //http://stackoverflow.com/questions/15369566/putting-space-in-camel-case-string-using-regular-expression
    return this.replace(/([A-Z])([A-Z])([a-z])|([a-z])([A-Z])/g, '$1$4 $2$3$5');
};

String.prototype.pluralize = function (pluralForm, count)
{
    if (typeof pluralForm === "undefined" || typeof count === "undefined") { return String(this); }

    var parsedCount = parseInt(count as any);
    return isNaN(parsedCount) || parsedCount === 1 ? String(this) : pluralForm;
};

function trimLeft(this: string)
{
    return this.toString().replace(/^\s+/, "");
}
// String.trimLeft is a non-standard synonym of trimStart()
// String.trimStart is unsupported in Safari < 12
(String.prototype as any).trimStart ??= trimLeft;
String.prototype.trimLeft ??= trimLeft;

function trimRight(this: string)
{
    return this.toString().replace(/\s+$/, "");
};
(String.prototype as any).trimEnd ??= trimRight;
String.prototype.trimRight ??= trimRight;

String.prototype.capitalize = function ()
{
    return this.charAt(0).toUpperCase() + this.slice(1);
};

String.prototype.uncapitalize = function ()
{
    return this.charAt(0).toLowerCase() + this.slice(1);
};

String.prototype.dot = function ()
{
    return "." + this;
};

Date.prototype.isValidDate = function ()
{
    return !isNaN(this.getTime());
};

Date.prototype.safeToISOString = function ()
{
    return this.isValidDate() ? this.toISOString() : "";
};

Date.prototype.safeToDateString = function ()
{
    return this.safeToISOString().slice(0, 10);
};

Date.prototype.addDays = function (days)
{
    var date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
};

Date.prototype.daysUntil = function (laterDate)
{
    var dayMilliseconds = 86400000; //1000 * 60 * 60 * 24

    var firstDateMilliseconds = this.getTime();
    var laterDateMilliseconds = laterDate.getTime();

    var difference = laterDateMilliseconds - firstDateMilliseconds;

    return Math.round(difference / dayMilliseconds);
};

Boolean.prototype.toDisplayString = function ()
{
    return this.valueOf() === true ? "True" : "False";
};

// not available in Safari < 17.4
Promise.withResolvers ??= function <T>()
{
    let resolve: (value: T | PromiseLike<T>) => void = (_: T | PromiseLike<T>) => {}
    let reject: (reason?: any) => void = () => {}

    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    return { promise, resolve, reject };
}

// not available in Safari < 15.4
Array.prototype.at ??= function <T>(this: Array<T>, index: number): T
{
    const relativeIndex = index < 0 ? this.length + index : index;
    return this[relativeIndex];
};

// not available in Safari < 15.4
Array.prototype.findLastIndex ??= function<T>(this: T[], predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: any): number
{
    for (let i = this.length - 1; i >= 0; i -= 1)
    {
        if (predicate.call(thisArg, this[i], i, this))
        {
            return i;
        }
    }
    return -1;
}

Array.prototype.findLast ??= function <T>(this: T[], predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: any): T | undefined
{
    const index = this.findLastIndex(predicate, thisArg);
    return index !== -1 ? this[index] : undefined;
}

export function partitionArray<T>(iterable: Iterable<T>, predicate: (value: T) => boolean = Boolean)
{
    const array: readonly T[] = Array.isArray(iterable) ? iterable : Array.from(iterable);
    const falseElems: T[] = array.constructor();
    const trueElems: T[] = array.constructor();
    for (const elem of array)
    {
        const target = predicate(elem) ? trueElems : falseElems;
        target.push(elem);
    }
    return [falseElems, trueElems];
}

export function minBy<T>(array: readonly T[], keyFn: (elem: T) => (number | undefined))
{
    let max: T | undefined;
    let maxVal: number = Infinity;
    for (const elem of array)
    {
        const val = keyFn(elem) ?? NaN;
        if (val < maxVal)
        {
            max = elem;
            maxVal = val;
        }
    }
    return max;
}

export function maxBy<T>(array: readonly T[],keyFn: (elem: T) => number | undefined)
{
    return minBy(array, (elem) =>
    {
        const v = keyFn(elem);
        return v === undefined ? undefined : -v;
    });
}

//disable animations if the browser is set to prefer reduced motion (typically iOS)
if (getPrefersReducedMotion())
{
    $.fx.off = true;
}

(function ()
{
    var defaultAnimationOptions =
    {
        duration: 230,
        easing: 'swing'
    };

    var debouncedNotifyResize = debounce(notifyResize, 10);

    type QueuedAnimationMap = Map<Element, JQuery.EffectsOptions<HTMLElement> | undefined>;
    const animationQueue =
    {
        open: new Map() as QueuedAnimationMap,
        close: new Map() as QueuedAnimationMap,
        promise: null as JQuery.Deferred<void> | null,
    };

    /**
     * Returns whether an element is hidden in and of itself.  In contrast to :visible, this does not consider whether parent elements are visible.
     * @param element The element to check
     * @returns boolean indicating whether the element is hidden
     */
    var isElementHidden = function (element: HTMLElement)
    {
        //based off https://github.com/jquery/jquery/blob/master/src/css/var/isHiddenWithinTree.js
        return element.style.display === "none" ||
            element.style.display === "" &&
            $.contains(document.documentElement, element) &&
            $.css(element, "display") === "none";
    };
    // isElementHidden can be accessed as a pseudo-selector: $().find(":inherentlyHidden")
    ($ as unknown as JQueryStaticInternals).expr.pseudos.inherentlyHidden = isElementHidden;

    let processQueueTimeout: number | undefined;

    const processQueueHandler = function ()
    {
        animationQueue.close.forEach(function (options, element)
        {
            var jQElement = $(element);

            // don't animate the close if a sibling is in the open animation queue
            var animateCloseAnimation = animationQueue.open.size === 0 || !jQElement.siblings().toArray().some(function (sibling)
            {
                return animationQueue.open.has(sibling);
            });

            if (animateCloseAnimation)
            {
                jQElement
                    .stop()
                    .slideUp($.extend({}, defaultAnimationOptions, options))
                    .promise()
                    .done(debouncedNotifyResize);
            }
            else
            {
                jQElement
                    .hide(options)
                    .finish()
                    .promise()
                    .done(debouncedNotifyResize);
            }
        });

        animationQueue.open.forEach(function (options, element)
        {
            $(element)
                .stop()
                .slideDown($.extend({}, defaultAnimationOptions, options))
                .promise()
                .done(debouncedNotifyResize);
        });

        animationQueue.open.clear();
        animationQueue.close.clear();
        if (animationQueue.promise)
        {
            animationQueue.promise.resolve();
            animationQueue.promise = null;
        }
        processQueueTimeout = undefined;
    };

    const queueAnimation = function (queue: QueuedAnimationMap, element: HTMLElement, options: JQuery.EffectsOptions<HTMLElement> | undefined)
    {
        queue.set(element, options);
        if (processQueueTimeout === undefined)
        {
            processQueueTimeout = window.setTimeout(processQueueHandler, 0);
        }
    };

    const dequeueAnimation = function (queue: QueuedAnimationMap, element: HTMLElement)
    {
        if (!queue.has(element))
        {
            return;
        }
        var options = queue.get(element);

        if (!$.isEmptyObject(options))
        {
            //options can have callback functions so we want to call the callback functions without affecting the visible state of the element.
            if (isElementHidden(element))
            {
                $(element).hide(options);
            }
            else
            {
                $(element).show(options);
            }
        }
        queue.delete(element);
    };

    $.fn.extend(
        {
            viewpartHtml: async function (this: JQuery, html: string | undefined)
            {
                if (html === undefined)
                {
                    return;
                }
                const processedHtml = await processViewPartHtml(html) as string;
                return this.html(processedHtml);
            }
        });

    //extend jquery to provide default animations for showing/hiding elements
    $.fn.extend(
        {
            animateOpen: function (this: JQuery, options?: JQuery.EffectsOptions<HTMLElement> | undefined)
            {
                if ($.isEmptyObject(options))
                {
                    options = undefined;
                }

                if ($.fx.off || isContentLoading())
                {
                    return this.show(options).finish();
                }

                return this.each(function ()
                {
                    var element = $(this).finish(); //call .finish() to complete animations that may be underway, in case of multiple calls in quick succession

                    //the element is being shown, remove it from the close queue
                    dequeueAnimation(animationQueue.close, this);

                    if (isElementHidden(this))
                    {
                        queueAnimation(animationQueue.open, this, options);
                    }
                    else
                    {
                        element.show(options).finish();
                    }
                });
            },
            animateClose: function (this: JQuery, options?: JQuery.EffectsOptions<HTMLElement> | undefined)
            {
                if ($.isEmptyObject(options))
                {
                    options = undefined;
                }

                if ($.fx.off || isContentLoading())
                {
                    return this.hide(options).finish();
                }

                return this.each(function ()
                {
                    var element = $(this).finish(); //call .finish() to complete animations that may be underway, in case of multiple calls in quick succession

                    //the element is being hidden, remove it from the open queue
                    dequeueAnimation(animationQueue.open, this);

                    if (!isElementHidden(this))
                    {
                        queueAnimation(animationQueue.close, this, options);
                    }
                    else
                    {
                        element.hide(options).finish();
                    }
                });
            },
            animateToggle: function (this: JQuery, options?: JQuery.EffectsOptions<HTMLElement> | undefined | boolean, boolOpen?: boolean | undefined)
            {
                if (typeof options === "boolean")
                {
                    boolOpen = options;
                    options = undefined;
                }

                return this.each(function ()
                {
                    var shouldAnimateOpen = typeof boolOpen === "boolean" ? boolOpen : isElementHidden(this);
                    queueAnimation(shouldAnimateOpen ? animationQueue.open : animationQueue.close, this, options as JQuery.EffectsOptions<HTMLElement> | undefined);
                });
            },
            animationPromise: function (this: JQuery)
            {
                if (this.toArray().some(function (elem) { return animationQueue.open.has(elem) || animationQueue.close.has(elem); }))
                {
                    if (!animationQueue.promise)
                    {
                        animationQueue.promise = $.Deferred();
                    }
                    var elems = this;
                    return animationQueue.promise.then(function ()
                    {
                        return elems.promise("fx");
                    });
                }
                else
                {
                    return this.promise("fx");
                }
            },
            isAnimatingClosed: function (this: JQuery)
            {
                return this.toArray().some(function (elem) { return animationQueue.close.has(elem); });
            },
            isAnimatingOpen: function (this: JQuery)
            {
                return this.toArray().some(function (elem) { return animationQueue.open.has(elem); });
            },
            isAnimating: function (this: JQuery)
            {
                return this.toArray().some(function (elem) { return animationQueue.open.has(elem) || animationQueue.close.has(elem); });
            }
        });
})();

// Polyfill for Safari < 15.4 as well as insecure contexts
if (!crypto.randomUUID)
{
    crypto.randomUUID = function ()
    {
        const keyBytes = new Uint8Array(16);
        window.crypto.getRandomValues(keyBytes);
        // UUID version 4
        keyBytes[6] = (keyBytes[6] & 0x0f) | 0x40;
        // Variant 1
        keyBytes[8] = (keyBytes[8] & 0x3f) | 0x80;
        const stringified = Array.prototype.map.call(keyBytes, function (b) { return ("0" + b.toString(16)).slice(-2); }).join("");
        return stringified.replace(/^(........)(....)(....)(....)(............)$/, "$1-$2-$3-$4-$5") as`${string}-${string}-${string}-${string}-${string}`;
    }
}

// Needed for Safari < 17.4
/*! groupby-polyfill. MIT License. Jimmy Wärting <https://jimmy.warting.se/opensource> */
/**
 * Groups elements from an iterable into an object based on a callback function.
 */
Object.groupBy ??= function groupBy<K extends PropertyKey, T>(iterable: Iterable<T>, callbackfn: (item: T, index: number) => K) {
    const obj: Partial<Record<K, T[]>> = Object.create(null);
    let i = 0;
    for (const value of iterable)
    {
        const key = callbackfn(value, i++)
        key in obj ? obj[key]!.push(value) : (obj[key] = [value])
    }
    return obj;
}

/**
 * Groups elements from an iterable into a Map based on a callback function.
 */
Map.groupBy ??= function groupBy<K, T>(iterable: Iterable<T>, callbackfn: (item: T, index: number) => K)
{
    const map = new Map<K, T[]>();
    let i = 0;
    for (const value of iterable)
    {
        const key = callbackfn(value, i++);
        const list = map.get(key);
        list ? list.push(value) : map.set(key, [value])
    }
    return map;
};

Array.chunk = function<T>(iterable: Iterable<T>, count: number)
{
    let result: T[][] = [];
    let accum: T[] = [];
    for (const elem of iterable)
    {
        accum.push(elem);
        if (accum.length === count)
        {
            result.push(accum);
            accum = [];
        }
    }
    if (accum.length > 0)
    {
        result.push(accum);
    }
    return result;
};

(function ()
{
    let testElem: HTMLElement | undefined;
    // Polyfill of https://developer.mozilla.org/en-US/docs/Web/CSS/:is
    function makeIs(allowJquery: boolean)
    {
        return $.expr.createPseudo(function (selector: string)
        {
            if (selector.includes(":scope"))
            {
                // even if we could supply an alternate scope to matches(), we don't know what it was
                // meant to be
                throw new Error("The :scope selector is not supported by the :is polyfill")
            }
            const matcher = getMatcher(selector);
            if (matcher)
            {
                return matcher;
            }

            const supportedSelector = ($ as unknown as JQueryStaticInternals).find.tokenize(selector)
                .map((tokens: { value: string; }[]) => tokens.reduce((accum, token) => accum + token.value, ""))
                .filter((group: string) => !!getMatcher(group))
                .join(",");
            return getMatcher(supportedSelector);

            function getMatcher(selector: string)
            {
                if (!testElem)
                {
                    testElem = document.createElement("div");
                }
                try
                {
                    testElem.querySelector(selector);
                    return function (elem: Element) { return elem.matches(selector); };
                }
                catch { }
                if (allowJquery)
                {
                    try
                    {
                        ($ as unknown as JQueryStaticInternals).find.matchesSelector(testElem, selector);
                        return function (elem: Element) { return ($ as unknown as JQueryStaticInternals).find.matchesSelector(elem, selector); };
                    }
                    catch { }
                }
                return null as unknown as any;
            }
        });
    };
    // For forwards compatibility, don't support jquery-defined selectors.
    // :is uses a "forgiving selector list" and will silently ignore selectors that are invalid in the browser,
    // like :tabbable.
    const is = makeIs(false);
    const pseudos = $.expr.pseudos;
    pseudos.is = is;
    pseudos.where = is;

    // jqIs supports jquery-defined selectors like :tabbable and :visible.
    pseudos.jqIs = makeIs(true);
})();

if ($.ui)
{
    // Default resizable handles to zIndex = auto
    $.extend(($.ui as unknown as JQueryUIStaticInternals).resizable.prototype.options, { zIndex: "auto" });
}

export function encodeHtml(value: string | number | boolean | null | undefined)
{
    return $("<div/>").text(value ?? "").html();
}

export function decodeHtml(value: string | null | undefined)
{
    return $("<div/>").html(value ?? "").text();
}

export let containsHtml = function (value: string)
{
    var decodedVal = skyward.extensions.decodeHtml(value);
    return decodedVal !== value;
};

// Replacement for $.isNumeric, which was removed in jQuery 4.0.
// Adapted from https://github.com/jquery/jquery/blob/3.6.3/src/core/isNumeric.js
export let isNumeric = function (value: unknown): boolean
{
    var type = typeof value;
    return (type === "number" || type === "string") && !isNaN((value as number) - parseFloat(value as string));
};

//createCache<TKey, TValue>(requestFunction: (key: TKey) => TValue):
export let createCache = function<TKey, TValue>(requestFunction: (key: TKey) => TValue)
{
    const cache = new Map<TKey, TValue>();
    const cacheFunction = function (this: any, key: TKey)
    {
        if (!cache.has(key))
        {
            cache.set(key, requestFunction.apply(this, arguments as unknown as [any]));
        }

        return cache.get(key)!;
    };

    cacheFunction.insert = function (key: TKey, val: TValue)
    {
        cache.set(key, val);
    };
    cacheFunction.remove = function (key: TKey)
    {
        cache.delete(key);
    };
    return cacheFunction;
};

/**
* Gets an object that stores a handle to an ajax request and aborts the
* request if another request is made before the previous one returns.
* @returns {SingleRequestHandler} A SingleRequestHandler object
*/
export let getSingleRequestHandler = function ()
{
    let request: JQuery.jqXHR | null = null;
    const requestHandler =
    {
        get request() { return request; },
        set request(value)
        {
            this.closeOpenRequest();
            request = isAjaxRequest(value) ? value : null;
        },
        closeOpenRequest: function ()
        {
            if (isAjaxRequest(request) && request.readyState !== 4 /* DONE */)
            {
                request.abort();
            }
            request = null;
        }
    };
    return requestHandler;

    function isAjaxRequest(request: JQuery.jqXHR | null): request is JQuery.jqXHR
    {
        return (request || {}).hasOwnProperty("readyState");
    }
};

/**
 * Creates a handler for managing sequential async operations with automatic cancellation.
 * Ensures last-write-wins semantics by cancelling actions from the prior run.
 *
 * @example
 * const handler = new SequentialOperationHandler();
 *
 * async function setValue(value) {
 *   return handler.run(async (signal) => {
 *     doSetup();
 *
 *     // Handle cancellation of ajax requests
 *     const result = await $.ajax({ url: "example.com", signal: signal });
 *     // Alternatively:
 *     const result2 = await $.ajax({ url: "example.com", useVueScope: true });
 *
 *     // Handle cancellation while awaiting an async method
 *     const processed = await process(result);
 *     if (signal.aborted) { return; }
 *
 *     // Apply result
 *     applyResult(result);
 *   });
 * }
 */
export class SequentialOperationHandler
{
    detached: boolean;
    scope: EffectScope | undefined;

    constructor(detached: boolean = false)
    {
        this.detached = detached;
    }

    run<T>(fn: (signal: AbortSignal) => JQuery.Promise<T> | JQuery.jqXHR): Promise<T>;
    run<T>(fn: (signal: AbortSignal) => Promise<T>): Promise<T>;
    run<T>(fn: (signal: AbortSignal) => T): Promise<Awaited<T>>;
    async run<T>(fn: (signal: AbortSignal) => Awaited<T>): Promise<T | null>
    {
        this.close();
        this.scope = effectScope(this.detached);
        const currentScope = this.scope;
        const result = await this.scope.run(async () =>
        {
            const controller = new AbortController();

            onScopeDispose(() =>
            {
                controller.abort();
            });

            try
            {
                return await fn(controller.signal);
            }
            catch (e)
            {
                const isAjaxAbort = e?.statusText === "abort";
                if (!(e instanceof CancellationError) && !isAjaxAbort)
                {
                    throw e;
                }
                return null;
            }
        })!;
        if (currentScope === this.scope)
        {
            this.close();
        }
        return result;
    }
    close()
    {
        this.scope?.stop();
        this.scope = undefined;
    }
}

/**
 * Returns an element's data attributes as an object, without parsing them as $elem.data() does.
 * Like $elem.data(), if it is passed a jquery collection with multiple elements it will return
 * the first one's data, and if it is passed an empty collection it will return undefined.
 * @param elem The element to read
 * @returns The element's data
 */
export let rawData = function (elem: Element | JQuery)
{
    const domElem = elem instanceof $ ? (elem as JQuery)[0] as Element | undefined : elem as Element
    if (typeof domElem === "undefined")
    {
        return undefined;
    }
    var result: Dictionary<string, string> = {};
    for (var i = 0; i < domElem.attributes.length; i++)
    {
        var attr = domElem.attributes[i];
        if (attr.name.startsWith("data-"))
        {
            result[camelCase(attr.name.slice(5))] = attr.value;
        }
    }
    return result;
};

export function camelCase(str: string)
{
    return str.replace(/-([a-z])/g, function (_match, letter)
    {
        return letter.toUpperCase();
    });
}

export function nextAutoId()
{
    return autoIdGenerator.getNewId();
}

/**
 * Announce a message to a screenreader, by pasting it into an element with aria-live="polite". Calling this ensures the message will be announced,
 * by adding spaces to the new message if it matches the previously announced message.
 * @param target Element to write to
 * @param message Message to announce
 */
export function ensureLiveAreaAnnounced(target: JQuery, message: string)
{
    const oldText = target.text();
    let newText = message.trim();
    if (oldText.trim() == newText)
    {
        const nbsp = "\xa0";
        const hasSpace = oldText.endsWith(nbsp);
        newText = hasSpace ? oldText.slice(0, -1) : (oldText + nbsp);
    }
    target.text(newText);
}

let exceptionReported = false;

// This method is called when you append HTML containing a <script src="..."> tag into the DOM using JQuery.
($ as unknown as JQueryStaticInternals)._evalUrl = function (url: string, options: object, doc: Document)
{
    if (!exceptionReported)
    {
        exceptionReported = true;
        reportException(new SkywardException(
            "Asynchronous HTML request was not sent using `viewparthtml`. This will lead to slowdowns or errors when loading the page." +
            " To fix this, change the `dataType` parameter in your ajax request to \"viewparthtml\", or pass your html string to skyward.global.processViewPartHtml()."
        ));
        window.setTimeout(() => exceptionReported = false, 0);
    }

    // Adapted from https://github.com/jquery/jquery/blob/3.6.3/src/manipulation/_evalUrl.js
    let xhr: XMLHttpRequest;
    return $.ajax(
    {
        url: url,
        xhr: () =>
        {
            xhr = $.ajaxSettings.xhr?.() ?? new XMLHttpRequest();
            return xhr;
        },
		// Make this explicit, since user can override this through ajaxSetup (trac-11264)
		type: "GET",
		dataType: "script",
		cache: true,
		async: false,
		global: false,

		// Only evaluate the response if it is successful (gh-4126)
		// dataFilter is not invoked for failure responses, so using it instead
		// of the default converter is kludgy but it works.
        converters:
        {
			"text script": function() {}
		},
        dataFilter: function (response)
        {
            const urlInText = /\n\/\/# (sourceMappingURL|sourceURL)[^\n]*\n?$/.test(response);
            if (!urlInText)
            {
                const mapFromHeader = xhr.getResponseHeader("SourceMap");
                if (mapFromHeader)
                {
                    const relativeURL = new URL(mapFromHeader, window.location.href);
                    response += `\n//# sourceMappingURL=${relativeURL}`;
                }
                else
                {
                    response += `\n//# sourceURL=${url}`
                }
            }
			(jQuery.globalEval as Function)( response, options, doc );
		}
	});
};

// Monkey-patch removeData to work around https://github.com/jquery/jquery/issues/5751
const originalRemoveData = jQuery.removeData;
const originalRemoveDataPrototype = jQuery.fn.removeData;

jQuery.removeData = function (element, key)
{
    originalRemoveData.apply(jQuery, arguments as any);
    if (key === undefined)
    {
        (jQuery as any)._removeData(element, "hasDataAttrs");
    }
}
jQuery.fn.removeData = function (key)
{
    originalRemoveDataPrototype.apply(this, arguments as any);
    if (key === undefined)
    {
        this.each(function ()
        {
            (jQuery as any)._removeData(this, "hasDataAttrs");
        })
    }
    return this;
};

/**
 * Checks if a string is null, undefined, or empty (after trimming whitespace)
 */
export function isNullOrEmpty(value: string | null | undefined): value is null | undefined | ""
{
    return value === null || value === undefined || value.trim() === "";
}
